import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomerListHeader from "./components/CustomerListHeader";
import CustomerListItem from "./components/CustomerListItem";
import CustomerListActions from "./components/CustomerListActions";
import {
  ActivateAccountModal,
  UpdateCustomerModal,
  SuspendAccountModal,
  SearchFilterModal,
} from "../../../shared/components/modals";
import { appActions } from "../../../redux/actions/in-app";
import { Customer } from "./utils/customerTypes";
import { LoadingItem } from "../../../shared/components/loading";
import ExportIco from "../../../assets/icons/export.png";
import "./CustomerList.scss";

interface CustomerListProps {
  getCustomers: typeof appActions.GetCustomers;
  toggleUser: typeof appActions.ToggleUser;
  updateCustomer: typeof appActions.UpdateCustomer;
  updateCustomerRequest: any;
  customersList: any;
  loading: boolean;
  error: string | null;
}

const CustomerList: React.FC<CustomerListProps> = ({
  getCustomers,
  toggleUser,
  updateCustomer,
  updateCustomerRequest,
  customersList,
  loading,
  error,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterCount, setFilterCount] = useState(0);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>("");

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState({
    province: [] as string[],
    city: "",
    startDate: "",
    endDate: "",
  });

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const customers = customersList?.results || [];
  const totalCount = customersList?.totalNumberOfRecords || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const buildPayload = (query = searchQuery, currentFilters = filters) => {
    let payload = `?Page=${currentPage}&Size=${itemsPerPage}`;
    if (query) payload += `&SearchQuery=${encodeURIComponent(query)}`;
    if (currentFilters.province.length) {
      const provinceQuery = currentFilters.province
        .map((p) => `Province=${encodeURIComponent(p)}`)
        .join("&");
      payload += `&${provinceQuery}`;
    }
    if (currentFilters.city)
      payload += `&City=${encodeURIComponent(currentFilters.city)}`;
    if (currentFilters.startDate)
      payload += `&StartDate=${encodeURIComponent(currentFilters.startDate)}`;
    if (currentFilters.endDate)
      payload += `&EndDate=${encodeURIComponent(currentFilters.endDate)}`;
    return payload;
  };

  // Stable fetch function — does NOT go into any useEffect dependency array
  const fetchCustomers = React.useCallback(
    (query = searchQuery, currentFilters = filters) => {
      getCustomers(buildPayload(query, currentFilters), false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCustomers],
  );

  // Auto-fetch when page or items-per-page changes
  useEffect(() => {
    getCustomers(buildPayload(), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  // Initial fetch on mount
  useEffect(() => {
    getCustomers(buildPayload(), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Only trigger search if query is 3+ characters or if it's cleared
    if (query.length >= 3 || query.length === 0) {
      searchTimeout.current = setTimeout(() => {
        fetchCustomers(query);
      }, 500);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    fetchCustomers("");
  };

  const handleFilter = () => {
    setShowFilterModal(true);
  };

  const handleApplyFilters = (newFilters: any) => {
    setFilters(newFilters);
    const count =
      (newFilters.province?.length || 0) +
      (newFilters.city ? 1 : 0) +
      (newFilters.startDate || newFilters.endDate ? 1 : 0);
    setFilterCount(count);
    fetchCustomers(searchQuery, newFilters);
  };

  const handleViewCustomer = (customerId: string) => {
    navigate(`/app/customers/${customerId}`);
  };

  const handleEditCustomer = (customerId: string) => {
    const customer = customers.find((c: Customer) => c.id === customerId);
    if (customer) {
      setSelectedCustomerId(customerId);
      setSelectedCustomerName(`${customer.firstName} ${customer.lastName}`);
      updateCustomer("", "CLEAR"); // Reset update status
      setShowEditModal(true);
    }
  };

  const handleSuspendCustomer = (customerId: string) => {
    const customer = customers.find((c: Customer) => c.id === customerId);
    if (customer) {
      setSelectedCustomerId(customerId);
      setSelectedCustomerName(`${customer.firstName} ${customer.lastName}`);
      setShowSuspendModal(true);
    }
  };

  const handleActivateCustomer = (customerId: string) => {
    const customer = customers.find((c: Customer) => c.id === customerId);
    if (customer) {
      setSelectedCustomerId(customerId);
      setSelectedCustomerName(`${customer.firstName} ${customer.lastName}`);
      setShowActivateModal(true);
    }
  };

  const handleConfirmSuspend = (reason: string) => {
    toggleUser(`?Id=${selectedCustomerId}`);
    setShowSuspendModal(false);
  };

  const handleConfirmActivate = () => {
    toggleUser(`?Id=${selectedCustomerId}`);
    setShowActivateModal(false);
  };

  const handleModalHide = React.useCallback(() => {
    setShowEditModal(false);
    fetchCustomers();
  }, [fetchCustomers]);

  const handleUpdateCustomer = (data: any) => {
    updateCustomer(selectedCustomerId, data);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortCustomersData = (dataToSort: Customer[]) => {
    if (!sortColumn) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "name":
          aValue = `${a.firstName} ${a.lastName}`.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case "address":
          aValue = a.address
            ? `${a.address.city}, ${a.address.state}`.toLowerCase()
            : "";
          bValue = b.address
            ? `${b.address.city}, ${b.address.state}`.toLowerCase()
            : "";
          break;
        case "spend":
          aValue = a.totalSpend || 0;
          bValue = b.totalSpend || 0;
          break;
        case "rating":
          aValue = a.averageRating || 0;
          bValue = b.averageRating || 0;
          break;
        case "email":
          aValue = (a.email || "").toLowerCase();
          bValue = (b.email || "").toLowerCase();
          break;
        case "date":
          aValue = new Date(a.dateCreated).getTime();
          bValue = new Date(b.dateCreated).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const renderSortableHeader = (
    column: string,
    label: string,
    className: string = "",
  ) => (
    <th
      className={`sortable-header ${className}`}
      onClick={() => handleSort(column)}
    >
      <div className="header-content">
        <span>{label}</span>
        <div className="sort-icons">
          {sortColumn === column ? (
            sortDirection === "asc" ? (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon active"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            )
          ) : (
            <>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M1.86835 0.499999C2.25325 -0.166667 3.2155 -0.166667 3.6004 0.5L5.33245 3.5C5.71735 4.16667 5.23623 5 4.46643 5H1.00232C0.232523 5 -0.248601 4.16667 0.136299 3.5L1.86835 0.499999Z"
                  fill="currentColor"
                />
              </svg>
              <svg
                width="6"
                height="5"
                viewBox="0 0 6 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sort-icon"
              >
                <path
                  d="M3.6004 4.5C3.2155 5.16667 2.25325 5.16667 1.86835 4.5L0.136298 1.5C-0.248602 0.833333 0.232524 0 1.00232 0L4.46643 0C5.23623 0 5.71735 0.833333 5.33245 1.5L3.6004 4.5Z"
                  fill="currentColor"
                />
              </svg>
            </>
          )}
        </div>
      </div>
    </th>
  );

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const sortedCustomers = sortCustomersData(customers);

  return (
    <div className="customer-list-container">
      <div className="customer-list-header-section">
        <h2 className="page-title">
          Customers <span>{totalCount}</span>
        </h2>
        <button className="export-customer-btn">
          <img src={ExportIco} alt="Export" />
          Export customers information
        </button>
      </div>

      <CustomerListHeader
        searchQuery={searchQuery}
        onSearch={handleSearch}
        onClearSearch={handleClearSearch}
        onFilter={handleFilter}
        filterCount={filterCount}
      />

      <SearchFilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />

      <div className="customer-list-table-container">
        <table className="customer-list-table">
          <thead>
            <tr>
              {renderSortableHeader("name", "Name", "customer-name-header")}
              {renderSortableHeader(
                "address",
                "Address",
                "customer-address-header",
              )}
              {renderSortableHeader(
                "spend",
                "Total spend",
                "customer-spend-header",
              )}
              {renderSortableHeader(
                "rating",
                "Average rating",
                "customer-rating-header",
              )}
              {renderSortableHeader("email", "Email", "customer-email-header")}
              {renderSortableHeader(
                "date",
                "Created on",
                "customer-date-header",
              )}
            </tr>
          </thead>
          <tbody className={loading ? "loading" : ""}>
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingItem />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={7}>
                  <div className="error-message">
                    Error loading customers: {error}
                  </div>
                </td>
              </tr>
            ) : sortedCustomers.length > 0 ? (
              sortedCustomers.map((customer: Customer) => (
                <CustomerListItem
                  key={customer.id}
                  customer={customer}
                  onView={handleViewCustomer}
                  onEdit={handleEditCustomer}
                  onSuspend={handleSuspendCustomer}
                  onActivate={handleActivateCustomer}
                />
              ))
            ) : (
              <tr>
                <td colSpan={7} className="no-customers">
                  No customers found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CustomerListActions
        totalCustomers={totalCount}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Modals */}
      <UpdateCustomerModal
        show={showEditModal}
        onHide={handleModalHide}
        onUpdate={handleUpdateCustomer}
        updateStatus={updateCustomerRequest}
        customerData={customers.find((c: any) => c.id === selectedCustomerId)}
      />

      <SuspendAccountModal
        show={showSuspendModal}
        onHide={() => setShowSuspendModal(false)}
        customerName={selectedCustomerName}
        onConfirm={handleConfirmSuspend}
      />

      <ActivateAccountModal
        show={showActivateModal}
        onHide={() => setShowActivateModal(false)}
        customerName={selectedCustomerName}
        onConfirm={handleConfirmActivate}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  customersList: state.allInAppReducers.getCustomersListReducer,
  loading: state.allInAppReducers.getCustomersListReducer?.loading,
  error: state.allInAppReducers.getCustomersListReducer?.error,
  updateCustomerRequest: state.allInAppReducers.updateCustomerReducer,
});

const mapDispatchToProps = {
  getCustomers: appActions.GetCustomers,
  toggleUser: appActions.ToggleUser,
  updateCustomer: appActions.UpdateCustomer,
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerList);
